'use client';

import { useEffect, useRef } from 'react';


interface ComparisonCard {
  id: string;
  problem: {
    label: string;
    description: string;
    icon: React.ReactNode;
  };
  solution: {
    label: string;
    description: string;
    icon: React.ReactNode;
  };
}

const comparisonCards: ComparisonCard[] = [
  {
    id: 'pay-to-win',
    problem: {
      label: 'PAY-TO-WIN ADVANTAGES',
      description: 'Many servers sell gameplay advantages that destroy fairness and competitive integrity.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    solution: {
      label: 'COSMETIC-ONLY SUPPORTER PERKS',
      description: 'VANTA never sells gameplay advantages. Supporter perks are strictly cosmetic and quality-of-life, ensuring a level playing field for everyone.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  },
  {
    id: 'admin-abuse',
    problem: {
      label: 'ADMIN ABUSE',
      description: 'Some servers suffer from admin favoritism, corruption, or unfair intervention.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    solution: {
      label: 'ACTIVE AND FAIR ADMIN TEAM',
      description: 'VANTA admins follow strict internal standards and focus on enforcing rules fairly without interfering in normal gameplay.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  },
  {
    id: 'unfair-wipes',
    problem: {
      label: 'UNFAIR OR RANDOM WIPES',
      description: 'Unpredictable wipes ruin progression and waste player effort.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    solution: {
      label: 'CONSISTENT AND TRANSPARENT WIPE SCHEDULE',
      description: 'VANTA follows a clear and reliable wipe schedule so players can plan and compete fairly.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  },
  {
    id: 'poor-performance',
    problem: {
      label: 'POOR PERFORMANCE AND LAG',
      description: 'Lag, desync, and unstable servers ruin fights and gameplay.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="18" y1="6" x2="22" y2="2" strokeLinecap="round"/>
          <line x1="2" y1="22" x2="6" y2="18" strokeLinecap="round"/>
        </svg>
      ),
    },
    solution: {
      label: 'OPTIMIZED HIGH-PERFORMANCE SERVERS',
      description: 'VANTA servers are optimized for smooth performance, low latency, and competitive gameplay.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  },
];

export default function WhyPlayersSwitched() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.08 }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="why-players-switched"
      ref={sectionRef}
      className="relative py-32 px-6"
      aria-labelledby="why-players-switched-heading"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 100px, rgba(106,92,255,0.02) 100px, rgba(106,92,255,0.02) 101px)',
        }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center gap-6 mb-20">
          <div className="reveal-hidden text-center">
            <div className="section-label mb-5">Why Players Switched</div>
            <h2
              id="why-players-switched-heading"
              className="font-heading text-5xl md:text-6xl font-900 text-white leading-tight"
              style={{ fontWeight: 900 }}
            >
              WHY PLAYERS SWITCHED TO{' '}
              <span className="gradient-text-subtle">VANTA</span>
            </h2>
          </div>
          <p className="text-lg text-vanta-muted max-w-2xl leading-relaxed reveal-hidden text-center">
            Players come to VANTA for a fair, competitive, and transparent Rust experience.
          </p>
        </div>

        {/* Comparison Cards Grid - Fixed Height System */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {comparisonCards?.map((card, index) => (
            <div
              key={card?.id}
              className="reveal-hidden group"
              style={{ transitionDelay: `${0.1 * (index + 1)}s` }}
            >
              {/* Fixed Height Card Container */}
              <div className="relative bg-[#14141a] rounded-3xl border-2 border-white/10 overflow-hidden transition-all duration-500 hover:border-vanta-accent/40 hover:shadow-[0_25px_70px_rgba(106,92,255,0.25)] h-full">
                {/* Grid Layout - Equal Height Columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                  {/* Problem Side - Left */}
                  <div className="relative p-8 flex flex-col h-full min-h-[320px] transition-colors duration-500" style={{ background: 'rgba(239, 68, 68, 0.08)' }}>
                    {/* Red accent border */}
                    <div
                      className="absolute top-0 left-0 w-1 h-full"
                      style={{ background: 'rgba(239, 68, 68, 0.4)' }}
                      aria-hidden="true"
                    />
                    
                    {/* Icon - Fixed Position */}
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 flex-shrink-0"
                      style={{
                        background: 'rgba(239, 68, 68, 0.15)',
                        color: '#ef4444',
                        border: '2px solid rgba(239, 68, 68, 0.3)',
                      }}
                      aria-hidden="true"
                    >
                      {card?.problem?.icon}
                    </div>

                    {/* Label - Fixed Height */}
                    <h3
                      className="text-xs uppercase mb-4 flex-shrink-0"
                      style={{ 
                        color: '#fca5a5',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        lineHeight: 1.3,
                        minHeight: '2.6em'
                      }}
                    >
                      {card?.problem?.label}
                    </h3>

                    {/* Description - Flexible */}
                    <p 
                      className="text-sm flex-grow"
                      style={{
                        color: 'rgba(255, 255, 255, 0.75)',
                        lineHeight: 1.6,
                        maxWidth: '100%'
                      }}
                    >
                      {card?.problem?.description}
                    </p>
                  </div>

                  {/* Vertical Divider - Full Height */}
                  <div 
                    className="absolute left-1/2 top-0 w-px h-full hidden md:block"
                    style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                    aria-hidden="true"
                  />

                  {/* Solution Side - Right */}
                  <div
                    className="relative p-8 flex flex-col h-full min-h-[320px] transition-colors duration-500"
                    style={{ background: 'rgba(34, 197, 94, 0.08)' }}
                  >
                    {/* Green accent border */}
                    <div
                      className="absolute top-0 right-0 w-1 h-full transition-all duration-500 group-hover:w-2"
                      style={{ background: 'rgba(34, 197, 94, 0.5)' }}
                      aria-hidden="true"
                    />
                    
                    {/* Icon with green check - Fixed Position */}
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 flex-shrink-0 transition-transform duration-500 group-hover:scale-110"
                      style={{
                        background: 'rgba(34, 197, 94, 0.15)',
                        color: '#22c55e',
                        border: '2px solid rgba(34, 197, 94, 0.4)',
                      }}
                      aria-hidden="true"
                    >
                      {card?.solution?.icon}
                    </div>

                    {/* Label - Fixed Height */}
                    <h3
                      className="text-xs uppercase mb-4 flex-shrink-0 transition-colors duration-500 group-hover:text-[#4ade80]"
                      style={{ 
                        color: '#86efac',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        lineHeight: 1.3,
                        minHeight: '2.6em'
                      }}
                    >
                      {card?.solution?.label}
                    </h3>

                    {/* Description - Flexible */}
                    <p 
                      className="text-sm flex-grow transition-colors duration-500 group-hover:text-vanta-text"
                      style={{
                        color: 'rgba(255, 255, 255, 0.75)',
                        lineHeight: 1.6,
                        maxWidth: '100%'
                      }}
                    >
                      {card?.solution?.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}